/**
 * Owner: Masa Bayu
 * File: AuthorProfileScreen.tsx
 * Date:Time: 2026-10-23 00:11
 * Description: §412 — Author Detail awam (/author/:id) replika mockup Owner:
 *   banner + avatar + nama + "Author · genre", butang Follow + menu ⋮ (salin
 *   pautan / notifikasi buku), bio, statistik 2 sel (Books / Categories), tab
 *   Books/About/Reviews. Ganti sepenuhnya skrin §386/§389 lama.
 *   §466 S1 — sel "Avg. Rating" DAN "Followers" dibuang, lencana "verified"
 *   dicabut: tiada penarafan, kiraan audiens atau status verifikasi di hub;
 *   Follow tetap benar kerana ia simpanan setempat per-peranti.
 */

import { useMemo, useState, useSyncExternalStore } from 'react';
import type { ReactElement } from 'react';
import { qxCategoryName } from '@qx/qxread-core';
import { getCatalogVersion, subscribeCatalog } from '../../publication/catalog-port';
import { listMarketplaceBooks } from '../../home/marketplace/marketplace-projection';
import { getMarketplaceLocaleProfile } from '../../home/marketplace/marketplace-locale';
import { resolvePublicAuthor } from '../public/public-author-data';
import {
  subscribeAuthorFollows,
  getAuthorFollowSnapshot,
  toggleAuthorFollow,
} from '../public/public-author-follow-store';
import { AUTHOR_SCREEN_LABELS } from '../discovery/author-discovery-labels';
import type { AuthorScreensLabels } from '../discovery/author-discovery-labels';
import { AuthorAvatar } from '../discovery/AuthorAvatar';
import { AuthorFollowButton } from '../discovery/AuthorFollowButton';
import { AuthorMoreMenu } from './AuthorMoreMenu';
import { AuthorBooksPanel, AuthorAboutPanel, AuthorReviewsPanel } from './AuthorProfilePanels';
import type { PublicAuthor } from '../public/public-author-data';
import './author-profile.css';

export interface AuthorProfileScreenProps {
  readonly authorId: string;
  readonly onNavigate: (path: string) => void;
}

type ProfileTab = 'books' | 'about' | 'reviews';

export function AuthorProfileScreen({
  authorId,
  onNavigate,
}: AuthorProfileScreenProps): ReactElement {
  const catalogVersion = useSyncExternalStore(subscribeCatalog, getCatalogVersion);
  const books = useMemo(() => listMarketplaceBooks(), [catalogVersion]);
  const author = useMemo(() => resolvePublicAuthor(authorId, books), [authorId, books]);
  const follows = useSyncExternalStore(subscribeAuthorFollows, getAuthorFollowSnapshot);

  const profile = useMemo(() => getMarketplaceLocaleProfile(), []);
  const t = useMemo(() => AUTHOR_SCREEN_LABELS[profile.language], [profile]);
  const [tab, setTab] = useState<ProfileTab>('books');

  if (author === null) {
    return (
      <div className="qxapf">
        <div className="qxapf__notfound" role="status">
          <h1 className="qxapf__nf-title">{t.profile.notFoundTitle}</h1>
          <p className="qxapf__nf-sub">{t.profile.notFoundSub}</p>
          <button
            type="button"
            className="qxapf__nf-cta"
            onClick={() => {
              onNavigate('/authors');
            }}
          >
            {t.profile.backToAuthors}
          </button>
        </div>
      </div>
    );
  }

  return (
    <AuthorProfileBody
      author={author}
      following={follows.includes(author.id)}
      t={t}
      language={profile.language}
      tab={tab}
      onTab={setTab}
      onNavigate={onNavigate}
    />
  );
}

function AuthorProfileBody({
  author,
  following,
  t,
  language,
  tab,
  onTab,
  onNavigate,
}: {
  author: PublicAuthor;
  following: boolean;
  t: AuthorScreensLabels;
  language: 'en' | 'ms';
  tab: ProfileTab;
  onTab: (tab: ProfileTab) => void;
  onNavigate: (path: string) => void;
}): ReactElement {
  const primaryGenre = author.genres[0];
  // §466 S1 — tanpa genre (manifest belum kategori) baris peran hanya "Author",
  // bukan "Author · Books" yang membaca seperti label statistik.
  const roleLine =
    primaryGenre === undefined
      ? t.profile.authorWord
      : `${t.profile.authorWord} · ${qxCategoryName(primaryGenre, language)}`;
  return (
    <div className="qxapf">
      <section className="qxapf__hero">
        <div className="qxapf__banner" aria-hidden="true" />
        <div className="qxapf__id">
          <span className="qxapf__avatar">
            <AuthorAvatar initials={author.initials} seed={author.id} />
          </span>
          <div className="qxapf__idtext">
            <h1 className="qxapf__name">{author.name}</h1>
            <p className="qxapf__role">{roleLine}</p>
            <div className="qxapf__actions">
              <AuthorFollowButton
                variant="solid"
                following={following}
                onToggle={() => {
                  toggleAuthorFollow(author.id);
                }}
                followLabel={t.profile.follow}
                followingLabel={t.profile.following}
                ariaName={author.name}
              />
              <AuthorMoreMenu author={author} t={t} />
            </div>
          </div>
        </div>
      </section>

      {author.bio === null ? null : <p className="qxapf__bio">{author.bio}</p>}

      <section className="qxapf__stats" aria-label={t.profile.tabsAria}>
        <span className="qxapf__stat">
          <span className="qxapf__stat-icon" aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 5.5A1.5 1.5 0 015.5 4H11v16H5.5A1.5 1.5 0 014 18.5v-13zM20 5.5A1.5 1.5 0 0018.5 4H13v16h5.5a1.5 1.5 0 001.5-1.5v-13z" />
            </svg>
          </span>
          <span className="qxapf__stat-text">
            <span className="qxapf__stat-num">{author.books.length}</span>
            <span className="qxapf__stat-label">{t.profile.booksStat}</span>
          </span>
        </span>
        <span className="qxapf__stat">
          <span className="qxapf__stat-icon" aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 4.5h6.5V11H4zM13.5 4.5H20V11h-6.5zM4 13.5h6.5V20H4zM13.5 13.5H20V20h-6.5z" />
            </svg>
          </span>
          <span className="qxapf__stat-text">
            <span className="qxapf__stat-num">{author.genres.length}</span>
            <span className="qxapf__stat-label">{t.profile.categoriesStat}</span>
          </span>
        </span>
      </section>

      <nav className="qxapf__tabs" role="tablist" aria-label={t.profile.tabsAria}>
        {(
          [
            ['books', t.profile.tabBooks],
            ['about', t.profile.tabAbout],
            ['reviews', t.profile.tabReviews],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            id={`qxapf-tab-${id}`}
            aria-selected={tab === id}
            aria-controls={`qxapf-panel-${id}`}
            className="qxapf__tab"
            data-active={tab === id}
            onClick={() => {
              onTab(id);
            }}
          >
            {label}
          </button>
        ))}
      </nav>

      <div
        className="qxapf__panel"
        role="tabpanel"
        id={`qxapf-panel-${tab}`}
        aria-labelledby={`qxapf-tab-${tab}`}
      >
        {tab === 'books' ? (
          <AuthorBooksPanel author={author} t={t} language={language} onNavigate={onNavigate} />
        ) : null}
        {tab === 'about' ? (
          <AuthorAboutPanel author={author} t={t} language={language} onNavigate={onNavigate} />
        ) : null}
        {tab === 'reviews' ? <AuthorReviewsPanel t={t} /> : null}
      </div>
    </div>
  );
}
